import * as z from 'zod';
import { TIMEZONES, SUBSCRIBER_LOCAL_TIME } from '../../../constants/scheduling';
import { getUTCISOString } from '../../../utils/scheduling';
import { useForm } from 'react-hook-form';

export const scheduleFormSchema = z
  .object({
    scheduleType: z.enum(['draft', 'now', 'schedule']),
    scheduling: z
      .object({
        type: z.enum(['local', 'universal']),
        date: z.string().optional(),
        time: z.string().optional(),
        timezone: z.enum(['subscriber_local', ...Object.keys(TIMEZONES)]).optional(),
        pastTimezoneBehavior: z.enum(['send_immediately', 'send_next_day', 'skip']).optional(),
        unknownTimezoneBehavior: z.enum(['use_fallback', 'skip']).optional(),
        fallbackTimezone: z.enum(Object.keys(TIMEZONES) as [string, ...string[]]).optional()
      })
      .strip()
      .optional()
  })
  .strip()
  .superRefine((data, ctx) => {
    // Early return if not scheduling
    if (data.scheduleType !== 'schedule') {
      return;
    }

    // 1. Validate scheduling object exists
    if (!data.scheduling) {
      ctx.addIssue({
        code: 'custom',
        message: 'Scheduling details are required',
        path: ['scheduling']
      });
      return;
    }

    const scheduling = data.scheduling;

    // 2. Required base fields for scheduling
    const requiredBaseFields = ['date', 'time', 'timezone', 'type'] as const;
    for (const field of requiredBaseFields) {
      if (!scheduling[field]) {
        ctx.addIssue({
          code: 'custom',
          message: `${field} is required for scheduled messages`,
          path: ['scheduling', field]
        });
      }
    }

    // 3. Date/time validation
    if (scheduling.date && scheduling.time) {
      try {
        // For universal time, use selected timezone
        // For local time, use merchant's timezone for validation
        const validationTimezone =
          scheduling.timezone !== SUBSCRIBER_LOCAL_TIME
            ? scheduling.timezone!
            : Intl.DateTimeFormat().resolvedOptions().timeZone;

        const utcDateTime = getUTCISOString(scheduling.date, scheduling.time, validationTimezone);

        if (new Date(utcDateTime) <= new Date()) {
          ctx.addIssue({
            code: 'custom',
            message: 'Selected date/time cannot be in the past',
            path: ['scheduling', 'time']
          });
        }
      } catch (error) {
        console.error('Error validating date/time:', error);
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid date/time combination',
          path: ['scheduling', 'time']
        });
      }
    }

    // 4. Type-specific validations
    if (scheduling.type === 'local') {
      // Past timezone behavior required
      if (!scheduling.pastTimezoneBehavior) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must specify how to handle past timezones',
          path: ['scheduling', 'pastTimezoneBehavior']
        });
      }

      // Unknown timezone behavior required
      if (!scheduling.unknownTimezoneBehavior) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must specify how to handle unknown timezones',
          path: ['scheduling', 'unknownTimezoneBehavior']
        });
      }

      // Fallback timezone required if using fallback
      if (scheduling.unknownTimezoneBehavior === 'use_fallback' && !scheduling.fallbackTimezone) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must select a fallback timezone',
          path: ['scheduling', 'fallbackTimezone']
        });
      }
    } else {
      // Universal time must have a timezone
      if (!scheduling.timezone) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must select a timezone for universal time',
          path: ['scheduling', 'timezone']
        });
      }
      // Universal time cannot use subscriber_local
      if (scheduling.timezone === SUBSCRIBER_LOCAL_TIME) {
        ctx.addIssue({
          code: 'custom',
          message: 'Universal time must use a specific timezone',
          path: ['scheduling', 'timezone']
        });
      }
    }
  });

export type ScheduleFormData = z.infer<typeof scheduleFormSchema>;

export interface SchedulingInputsProps {
  form: ReturnType<typeof useForm<ScheduleFormData>>;
}
